'use strict';

import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import moment from 'moment';
import redirectURL from '../redirectURL';
import {
  COLORS,
  MULTI_SELECT_STYLES,
  safeArray,
  groupBy,
  distinctValues,
  sum,
  round1,
  baseChartOptions,
  baseAxisLine,
  baseValueAxis,
  buildPivot,
  GridCard,
  statusColorForPercent,
  statusClassForPercent,
  percentValueFormatter,
  percentCellClass,
} from './dashboardShared';
import './performanceDashboardModern.css';

/*
 * FTL Movement tab - the 3rd top-level dashboard, alongside Performance and
 * Transporter. Sourced from consignments/getFTLVehicleUtilization.
 *
 * Confirmed real fields (from an actual sample response): consigner_city,
 * consignee_city, dispatch_date, total_weight (kg), total_cbm,
 * vehicle_capacity (per-row WEIGHT capacity in kg, confirmed by matching
 * real truck capacities - e.g. "TATA ACE" -> 800, "32 F 15 MT" -> 15000),
 * truck_type, truck_no, transporter_name, truck_seal_entity, invoice_type,
 * lr_no (frequently blank at this truck-trip level), consignment_status.
 *
 * Weight Utilization % is fully computable per the PDF's own DAX
 * (Weight = MAX(vehicle_capacity in group) * Trips; Weight utilize % =
 * TotalWeight / Weight) - no external table needed, since vehicle_capacity
 * is already present per row.
 *
 * Volume/CBM Utilization % has no true rated-capacity table (the PDF's DAX
 * sources it from a separate "Vehicle Type Weight and Capacity Table" that
 * doesn't exist here) - by explicit choice, cbmCapacityByVehicleType stands
 * in for it: the max total_cbm actually carried per vehicle type, across
 * the whole filtered dataset. It's a real, computed proxy, not a rated
 * capacity - labeled "(proxy)" everywhere it's shown.
 *
 * "Trips" matches the DAX exactly: COUNTROWS(SUMMARIZE(truck_no, Dispatch
 * Date Only)) - distinct (truck_no, dispatch date) pairs, not a raw row
 * count. total_weight/total_cbm are summed directly across every row with
 * no trip-dedup, matching SUM(getFTLVehicleUtilization[total_weight]).
 */

const ALL_ROUTES_OPTION = { value: 'All', label: 'All Routes' };

const DATE_TYPE_OPTIONS = [
  { value: 'Dispatch', label: 'Dispatch Date' },
  { value: 'Invoice', label: 'Invoice Date' },
];

// "Trips" (dedupeByTrip) always uses the literal dispatch_date, per the
// PDF's fixed DAX definition (SUMMARIZE(truck_no, Dispatch Date Only)) -
// NOT toggled by Date Basis. Month-wise grouping/trend widgets, however,
// switch fields with the toggle, same as the other two dashboards.
function dateOnly(dispatchDate) {
  return dispatchDate ? moment(dispatchDate).format('YYYY-MM-DD') : null;
}

// invoice_time doesn't exist on this feed yet - the backend is adding it;
// this falls back to dispatch_date until then, then "auto-populates" once
// the field is live, with no further code change needed.
function dispatchDateOf(row, dateType) {
  const value = dateType === 'Invoice' ? row.invoice_time : (row.dispatch_date || row.invoice_time);
  return value ? moment(value) : null;
}

function monthKeyOf(m) {
  return m && m.isValid() ? m.format('MMM YYYY') : null;
}

function monthRange(start, end) {
  const months = [];
  let cursor = moment(start).startOf('month');
  const last = moment(end).startOf('month');
  while (cursor.isSameOrBefore(last)) {
    months.push(cursor.format('MMM YYYY'));
    cursor = cursor.add(1, 'month');
  }
  return months;
}

function routeOf(row) {
  return `${row.consigner_city || 'Unknown'} - ${row.consignee_city || 'Unknown'}`;
}

// Trips = COUNTROWS(SUMMARIZE(truck_no, Dispatch Date Only)) - one
// representative row per distinct (truck_no, dispatch-date) combination.
function dedupeByTrip(rows) {
  const seen = new Set();
  const result = [];
  rows.forEach((row) => {
    const key = `${row.truck_no}||${dateOnly(row.dispatch_date)}`;
    if (!seen.has(key)) { seen.add(key); result.push(row); }
  });
  return result;
}

function weightKgOf(row) {
  const n = Number(row.total_weight);
  return isNaN(n) ? 0 : n;
}

function cbmOf(row) {
  const n = Number(row.total_cbm);
  return isNaN(n) ? 0 : n;
}

function vehicleCapacityOf(row) {
  const n = Number(row.vehicle_capacity);
  return isNaN(n) ? 0 : n;
}

// Capacity = MAX(capacityFn in group) * Trips; Utilize % =
// DIVIDE(TotalValue, Capacity, 0) - matches the PDF DAX exactly.
function utilizationPct(rows, valueFn, capacityFn) {
  if (!rows.length) return 0;
  const totalValue = sum(rows.map(valueFn));
  const maxCapacity = Math.max(0, ...rows.map(capacityFn));
  const trips = dedupeByTrip(rows).length;
  const denom = maxCapacity * trips;
  return denom ? round1((totalValue / denom) * 100) : 0;
}

// Weight Utilization % - vehicle_capacity is a real per-row field.
function weightUtilizationPct(rows) {
  return utilizationPct(rows, weightKgOf, vehicleCapacityOf);
}

// Volume Utilization % - there's no CBM-capacity-per-vehicle-type master
// table, so cbmCapacityByVehicleType (max total_cbm actually observed per
// vehicle type, across the whole filtered dataset) stands in for it. Not a
// rated capacity - the best available real-data proxy per your call.
function volumeUtilizationPct(rows, cbmCapacityByVehicleType) {
  return utilizationPct(rows, cbmOf, (row) => cbmCapacityByVehicleType[row.truck_type || 'Unspecified'] || 0);
}

export default class FtlMovementDashboardModern extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      lastRefreshed: null,
      trendRoute: ALL_ROUTES_OPTION,

      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        entity: [],
        transporter: [],
        vehicleType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        fuelType: [],
        transporterCategory: [],
        invoiceType: [],
      },

      filterOptions: {
        entity: [],
        transporter: [],
        vehicleType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        fuelType: [],
        transporterCategory: [],
        invoiceType: [],
      },

      rawRows: [],
      filteredRows: [],
      derived: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  buildPayload() {
    const f = this.state.filters;
    return {
      startDate: moment(f.startDate).format('YYYY-MM-DD'),
      endDate: moment(f.endDate).format('YYYY-MM-DD'),
      // Backend has since added date_filter support to
      // ml/getFTLVehicleUtilization, mirroring getInvoiceData/
      // getDataForPowerBiDashBoard - "invoice" filters on invoice_time,
      // anything else filters on dispatch_date, matching the Date Basis
      // toggle.
      date_filter: f.dateType.value === 'Invoice' ? 'invoice' : 'dispatch',
    };
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    redirectURL.post('consignments/getFTLVehicleUtilization', this.buildPayload())
      .then((resp) => {
        const rows = safeArray(resp.data);
        const filterOptions = {
          entity: distinctValues(rows, 'truck_seal_entity').map((v) => ({ value: v, label: v })),
          transporter: distinctValues(rows, 'transporter_name').map((v) => ({ value: v, label: v })),
          vehicleType: distinctValues(rows, 'truck_type').map((v) => ({ value: v, label: v })),
          sourceZone: distinctValues(rows, 'consigner_zone').map((v) => ({ value: v, label: v })),
          sourceCity: distinctValues(rows, 'consigner_city').map((v) => ({ value: v, label: v })),
          destinationZone: distinctValues(rows, 'consignee_zone').map((v) => ({ value: v, label: v })),
          destinationCity: distinctValues(rows, 'consignee_city').map((v) => ({ value: v, label: v })),
          destinationState: distinctValues(rows, 'consignee_state').map((v) => ({ value: v, label: v })),
          fuelType: distinctValues(rows, 'fuel_type').map((v) => ({ value: v, label: v })),
          transporterCategory: distinctValues(rows, 'tpt_category').map((v) => ({ value: v, label: v })),
          invoiceType: distinctValues(rows, 'invoice_type').map((v) => ({ value: v, label: v })),
        };
        this.setState({ loading: false, lastRefreshed: moment(), rawRows: rows, filterOptions });
        this.applyClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching FTL vehicle utilization data:', error);
        this.setState({ loading: false, error: 'Unable to load FTL movement data. Please try again.' });
      });
  };

  applyClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.rawRows;
    const f = this.state.filters;
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      return selection.map((o) => o.value).includes(row[field]);
    };
    // Date range is now applied server-side (see buildPayload's
    // startDate/endDate/date_filter) - no client-side re-check needed here,
    // same as getDataForPowerBiDashBoard/getInvoiceData elsewhere.
    const filtered = rows.filter((row) => (
      matches(row, f.entity, 'truck_seal_entity')
      && matches(row, f.transporter, 'transporter_name')
      && matches(row, f.vehicleType, 'truck_type')
      && matches(row, f.sourceZone, 'consigner_zone')
      && matches(row, f.sourceCity, 'consigner_city')
      && matches(row, f.destinationZone, 'consignee_zone')
      && matches(row, f.destinationCity, 'consignee_city')
      && matches(row, f.destinationState, 'consignee_state')
      && matches(row, f.fuelType, 'fuel_type')
      && matches(row, f.transporterCategory, 'tpt_category')
      && matches(row, f.invoiceType, 'invoice_type')
    ));
    this.setState({ filteredRows: filtered, derived: this.computeDerived(filtered) });
  };

  computeDerived(rows) {
    const dateType = this.state.filters.dateType.value;
    const months = monthRange(this.state.filters.startDate, this.state.filters.endDate);
    const tripRows = dedupeByTrip(rows);

    // No CBM-capacity-per-vehicle-type master table exists, so this stands
    // in for it: max total_cbm actually carried per vehicle type, across
    // the whole filtered dataset - a real, computable proxy (not a rated
    // capacity) per an explicit call on this tradeoff.
    const vehicleTypeGroups = groupBy(rows, (r) => r.truck_type || 'Unspecified');
    const cbmCapacityByVehicleType = {};
    vehicleTypeGroups.forEach((items, name) => { cbmCapacityByVehicleType[name] = Math.max(0, ...items.map(cbmOf)); });

    // 1. Fleet Utilization (%) Overview - Weight% and Volume% by vehicle type.
    const fleetUtilization = Array.from(vehicleTypeGroups.entries())
      .map(([name, items]) => ({
        name,
        weightPct: weightUtilizationPct(items),
        volumePct: volumeUtilizationPct(items, cbmCapacityByVehicleType),
      }))
      .sort((a, b) => b.weightPct - a.weightPct);

    // 2. Route Wise Utilization (%) (Monthly) - Weight% and Volume%.
    const routePivot = buildPivot(rows, routeOf, months, (r) => monthKeyOf(dispatchDateOf(r, dateType)));
    const routes = Array.from(new Set(rows.map(routeOf)));
    const routeMonthWeightPct = {};
    const routeMonthVolumePct = {};
    routes.forEach((route) => {
      routeMonthWeightPct[route] = {};
      routeMonthVolumePct[route] = {};
      months.forEach((m) => {
        const cellRows = routePivot.cell(route, m);
        routeMonthWeightPct[route][m] = weightUtilizationPct(cellRows);
        routeMonthVolumePct[route][m] = volumeUtilizationPct(cellRows, cbmCapacityByVehicleType);
      });
    });
    const routeRowTotal = {};
    const routeRowVolumeTotal = {};
    routes.forEach((route) => {
      const routeRows = rows.filter((r) => routeOf(r) === route);
      routeRowTotal[route] = weightUtilizationPct(routeRows);
      routeRowVolumeTotal[route] = volumeUtilizationPct(routeRows, cbmCapacityByVehicleType);
    });

    // 3. Vehicle Master Wise Weights (KG) and CBM - Wt(KG) = MAX per-row
    // vehicle_capacity within each vehicle type (real). Vol(CBM) = max
    // total_cbm actually carried (proxy, not a rated capacity - see above).
    const vehicleMasterRows = Array.from(vehicleTypeGroups.entries())
      .map(([name, items]) => ({ name, wtKg: Math.max(0, ...items.map(vehicleCapacityOf)), volCbm: cbmCapacityByVehicleType[name] }))
      .sort((a, b) => b.wtKg - a.wtKg);

    // 4. Total Trips (Route)
    const tripsByRoute = groupBy(tripRows, routeOf);
    const totalTripsByRoute = Array.from(tripsByRoute.entries())
      .map(([name, items]) => ({ name, trips: items.length }))
      .sort((a, b) => b.trips - a.trips);

    // 5. Transporter Wise Total Trips (Monthly)
    const transporterTripPivot = buildPivot(tripRows, (r) => r.transporter_name || 'Unspecified', months, (r) => monthKeyOf(dispatchDateOf(r, dateType)));
    const transporters = Array.from(new Set(tripRows.map((r) => r.transporter_name || 'Unspecified')));

    // 6. Vehicle Type Wise Total Trips (Monthly)
    const vehicleTripPivot = buildPivot(tripRows, (r) => r.truck_type || 'Unspecified', months, (r) => monthKeyOf(dispatchDateOf(r, dateType)));
    const vehicleTypes = Array.from(new Set(tripRows.map((r) => r.truck_type || 'Unspecified')));

    // 7. Trend Analysis - Weight (KG) & CBM Transported (Route) - month-wise,
    // filterable to one route (or all).
    const routeOptions = [ALL_ROUTES_OPTION, ...routes.map((r) => ({ value: r, label: r }))];
    const trendRoute = this.state.trendRoute.value;
    const trendRows = trendRoute === 'All' ? rows : rows.filter((r) => routeOf(r) === trendRoute);
    const trendMonthGroups = groupBy(trendRows, (r) => monthKeyOf(dispatchDateOf(r, dateType)));
    const weightCbmTrend = {
      categories: months,
      weightKg: months.map((m) => round1(sum((trendMonthGroups.get(m) || []).map(weightKgOf)))),
      cbm: months.map((m) => round1(sum((trendMonthGroups.get(m) || []).map(cbmOf)))),
    };

    // 8. Trend Analysis - Weight & Volume Utilization (%)
    const allMonthGroups = groupBy(rows, (r) => monthKeyOf(dispatchDateOf(r, dateType)));
    const utilizationTrend = {
      categories: months,
      weightPct: months.map((m) => weightUtilizationPct(allMonthGroups.get(m) || [])),
      volumePct: months.map((m) => volumeUtilizationPct(allMonthGroups.get(m) || [], cbmCapacityByVehicleType)),
    };

    // 9. Summary - row-level dump. "Trips" is a 1/0 flag (1 on the first row
    // of each distinct truck_no+dispatch-date combo, 0 on the rest) so the
    // grid's footer total still equals the correct distinct trip count
    // without double-counting rows that share a trip.
    const seenTrip = new Set();
    const summaryRows = rows.map((r) => {
      const key = `${r.truck_no}||${dateOnly(r.dispatch_date)}`;
      const isFirst = !seenTrip.has(key);
      seenTrip.add(key);
      return { ...r, __tripFlag: isFirst ? 1 : 0 };
    });

    return {
      totalRecords: rows.length,
      totalTrips: tripRows.length,
      fleetUtilization,
      routeMonthWeightPct,
      routeMonthVolumePct,
      routeRowTotal,
      routeRowVolumeTotal,
      routes,
      months,
      vehicleMasterRows,
      totalTripsByRoute,
      transporterTripPivot,
      transporters,
      vehicleTripPivot,
      vehicleTypes,
      routeOptions,
      weightCbmTrend,
      utilizationTrend,
      summaryRows,
    };
  }

  onFilterChange = (key, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }));
  };

  // Apply/Reset now re-fetch from the server: getFTLVehicleUtilization
  // supports startDate/endDate/date_filter (confirmed by the backend), so a
  // date range or Date Basis change genuinely narrows the upstream query
  // instead of re-filtering an already-unbounded dataset client-side.
  onApply = () => { this.loadData(); };

  onReset = () => {
    this.setState({
      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        entity: [],
        transporter: [],
        vehicleType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        fuelType: [],
        transporterCategory: [],
        invoiceType: [],
      },
      trendRoute: ALL_ROUTES_OPTION,
    }, () => this.loadData());
  };

  onTrendRouteChange = (opt) => {
    this.setState({ trendRoute: opt }, () => this.applyClientFilters());
  };

  renderEmptyState(message, hint) {
    return (
      <div className="pdm-empty-state">
        <div className="pdm-empty-state-icon">&#9679;</div>
        <strong>{message}</strong>
        {hint && <span>{hint}</span>}
      </div>
    );
  }

  renderChartCard(title, subtitle, options, height = 320) {
    return (
      <div className="pdm-card">
        {title && <p className="pdm-card-title">{title}</p>}
        {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
        {options ? (
          <div className="pdm-chart-flex" style={{ height }}>
            <HighchartsReact
              highcharts={Highcharts}
              containerProps={{ style: { width: '100%', height: '100%' } }}
              options={{ ...options, chart: { ...options.chart, height } }}
              updateArgs={[true, true, true]}
            />
          </div>
        ) : this.renderEmptyState('No data available', 'Try widening the date range or clearing a filter.')}
      </div>
    );
  }

  buildFleetUtilizationOptions(fleetUtilization) {
    if (!fleetUtilization.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'column' },
      title: { text: null },
      xAxis: baseAxisLine(fleetUtilization.map((f) => f.name)),
      // No hardcoded max - Weight Utilization % can legitimately exceed
      // 100% (overloaded vs rated capacity). A fixed max clipped those bars
      // to the same rendered height as a genuine 100% bar and pushed their
      // data labels off the plot area. Auto-scale instead, with a 100%
      // reference line so the capacity benchmark stays visible.
      yAxis: {
        ...baseValueAxis('Utilization %'),
        plotLines: [{ value: 100, color: COLORS.red, width: 1, dashStyle: 'Dash', zIndex: 5, label: { text: '100% capacity', style: { color: COLORS.red, fontSize: '10px' } } }],
      },
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { series: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, format: '{point.y}%', style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { shared: true, valueSuffix: '%' },
      series: [
        { name: 'Weight Utilization %', data: fleetUtilization.map((f) => ({ y: f.weightPct, color: statusColorForPercent(f.weightPct) })), color: COLORS.blue },
        { name: 'Volume Utilization % (proxy)', data: fleetUtilization.map((f) => f.volumePct), color: COLORS.orange },
      ],
    };
  }

  buildWeightCbmTrendOptions(trend) {
    if (!trend.categories.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'spline' },
      title: { text: null },
      xAxis: baseAxisLine(trend.categories),
      yAxis: [
        { title: { text: 'Total Weight (KG)', style: { color: COLORS.blue, fontSize: '11px' } }, gridLineColor: COLORS.grid, labels: { style: { color: COLORS.muted, fontSize: '11px' } } },
        { title: { text: 'Total CBM', style: { color: COLORS.orange, fontSize: '11px' } }, gridLineColor: COLORS.grid, labels: { style: { color: COLORS.muted, fontSize: '11px' } }, opposite: true },
      ],
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { series: { lineWidth: 2, marker: { radius: 4 }, connectNulls: true } },
      tooltip: { shared: true },
      series: [
        { name: 'Total Weight (KG)', data: trend.weightKg, color: COLORS.blue, yAxis: 0 },
        { name: 'Total CBM', data: trend.cbm, color: COLORS.orange, yAxis: 1 },
      ],
    };
  }

  buildUtilizationTrendOptions(trend) {
    if (!trend.categories.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'spline' },
      title: { text: null },
      xAxis: baseAxisLine(trend.categories),
      // Same fix as Fleet Utilization (%) Overview - no hardcoded max, since
      // Weight Utilization % can legitimately exceed 100%.
      yAxis: {
        ...baseValueAxis('Utilization %'),
        plotLines: [{ value: 100, color: COLORS.red, width: 1, dashStyle: 'Dash', zIndex: 5, label: { text: '100% capacity', style: { color: COLORS.red, fontSize: '10px' } } }],
      },
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { series: { lineWidth: 2, marker: { radius: 4 }, connectNulls: true } },
      tooltip: { shared: true, valueSuffix: '%' },
      series: [
        { name: 'Weight Utilization %', data: trend.weightPct, color: COLORS.blue },
        { name: 'Volume Utilization % (proxy)', data: trend.volumePct, color: COLORS.orange },
      ],
    };
  }

  buildRouteUtilizationGrid(d) {
    const records = d.routes.map((route) => {
      const row = { route };
      d.months.forEach((m) => {
        row[`${m}__weight`] = d.routeMonthWeightPct[route][m];
        row[`${m}__volume`] = d.routeMonthVolumePct[route][m];
      });
      row.totalWeight = d.routeRowTotal[route];
      row.totalVolume = d.routeRowVolumeTotal[route];
      return row;
    });
    const columnDefs = [
      { headerName: 'Route', field: 'route', pinned: 'left', width: 220 },
      ...d.months.map((m) => ({
        headerName: m,
        children: [
          { headerName: 'Weight %', field: `${m}__weight`, width: 110, valueFormatter: percentValueFormatter, cellClass: percentCellClass },
          { headerName: 'Volume %', field: `${m}__volume`, width: 110, valueFormatter: percentValueFormatter, cellClass: percentCellClass, headerTooltip: 'Proxy: actual CBM moved vs max CBM observed for that vehicle type - not a rated capacity.' },
        ],
      })),
      { headerName: 'Total Weight %', field: 'totalWeight', width: 140, pinned: 'right', cellClass: (p) => ['pdm-ag-strong', statusClassForPercent(p.value)], valueFormatter: percentValueFormatter },
      { headerName: 'Total Volume %', field: 'totalVolume', width: 140, pinned: 'right', cellClass: (p) => ['pdm-ag-strong', statusClassForPercent(p.value)], valueFormatter: percentValueFormatter },
    ];
    return { columnDefs, records, pinnedBottomRowData: null };
  }

  buildVehicleMasterGrid(d) {
    const records = d.vehicleMasterRows.map((v) => ({ vehicleType: v.name, volCbm: v.volCbm, wtKg: v.wtKg }));
    const columnDefs = [
      { headerName: 'Vehicle Type', field: 'vehicleType', pinned: 'left', width: 200 },
      { headerName: 'Vol (CBM)', field: 'volCbm', width: 130, headerTooltip: 'Max total_cbm actually carried per vehicle type - a proxy, not a rated capacity table.' },
      { headerName: 'Wt (KG)', field: 'wtKg', width: 130 },
    ];
    const pinnedBottomRowData = [{
      vehicleType: 'Total',
      volCbm: round1(sum(d.vehicleMasterRows.map((v) => v.volCbm))),
      wtKg: round1(sum(d.vehicleMasterRows.map((v) => v.wtKg))),
    }];
    return { columnDefs, records, pinnedBottomRowData };
  }

  buildTotalTripsRouteGrid(d) {
    const records = d.totalTripsByRoute.map((r) => ({ route: r.name, trips: r.trips }));
    const columnDefs = [
      { headerName: 'Route', field: 'route', pinned: 'left', width: 260 },
      { headerName: 'Trips', field: 'trips', width: 120 },
    ];
    const pinnedBottomRowData = [{ route: 'Total', trips: sum(d.totalTripsByRoute.map((r) => r.trips)) }];
    return { columnDefs, records, pinnedBottomRowData };
  }

  buildTransporterTripsGrid(d) {
    const records = d.transporters.map((t) => {
      const row = { transporter: t };
      let rowTotal = 0;
      d.months.forEach((m) => {
        const count = d.transporterTripPivot.cell(t, m).length;
        row[m] = count;
        rowTotal += count;
      });
      row.total = rowTotal;
      return row;
    });
    const columnDefs = [
      { headerName: 'Transporter', field: 'transporter', pinned: 'left', width: 200 },
      ...d.months.map((m) => ({ headerName: m, field: m, width: 110 })),
      { headerName: 'Total', field: 'total', width: 110, pinned: 'right', cellClass: 'pdm-ag-strong' },
    ];
    const colTotals = { transporter: 'Total' };
    d.months.forEach((m) => { colTotals[m] = sum(records.map((r) => r[m])); });
    colTotals.total = sum(records.map((r) => r.total));
    return { columnDefs, records, pinnedBottomRowData: [colTotals] };
  }

  buildVehicleTypeTripsGrid(d) {
    const records = d.vehicleTypes.map((v) => {
      const row = { vehicleType: v };
      let rowTotal = 0;
      d.months.forEach((m) => {
        const count = d.vehicleTripPivot.cell(v, m).length;
        row[m] = count;
        rowTotal += count;
      });
      row.total = rowTotal;
      return row;
    });
    const columnDefs = [
      { headerName: 'Vehicle Type', field: 'vehicleType', pinned: 'left', width: 180 },
      ...d.months.map((m) => ({ headerName: m, field: m, width: 110 })),
      { headerName: 'Total', field: 'total', width: 110, pinned: 'right', cellClass: 'pdm-ag-strong' },
    ];
    const colTotals = { vehicleType: 'Total' };
    d.months.forEach((m) => { colTotals[m] = sum(records.map((r) => r[m])); });
    colTotals.total = sum(records.map((r) => r.total));
    return { columnDefs, records, pinnedBottomRowData: [colTotals] };
  }

  buildSummaryColumnDefs() {
    return [
      { headerName: 'Date', field: 'dispatch_date', pinned: 'left', width: 150, valueGetter: (p) => (p.data.dispatch_date ? moment(p.data.dispatch_date).format('DD-MMM-YYYY') : '') },
      { headerName: 'LR No', field: 'lr_no', width: 130 },
      { headerName: 'Transporter', field: 'transporter_name', width: 180 },
      { headerName: 'Vehicle No', field: 'truck_no', width: 130 },
      { headerName: 'Truck Type', field: 'truck_type', width: 140 },
      { headerName: 'Total Weight (KG)', field: 'total_weight', width: 150 },
      { headerName: 'Total CBM', field: 'total_cbm', width: 120 },
      { headerName: 'Vehicle Capacity', field: 'vehicle_capacity', width: 140 },
      { headerName: 'Entity', field: 'truck_seal_entity', width: 120 },
      { headerName: 'Source City', field: 'consigner_city', width: 130 },
      { headerName: 'Destination City', field: 'consignee_city', width: 140 },
      { headerName: 'Invoice Type', field: 'invoice_type', width: 150 },
      { headerName: 'Trips', field: '__tripFlag', width: 90, headerTooltip: '1 on the first row of each distinct vehicle+dispatch-date trip, 0 on the rest, so the total below is the correct distinct trip count.' },
    ];
  }

  render() {
    const { state } = this;
    const f = state.filters;
    const fo = state.filterOptions;
    const d = state.derived;

    return (
      <>
        {state.loading && (
          <div className="pdm-loading-overlay"><div className="pdm-spinner" /></div>
        )}
        {state.error && <div className="pdm-error-banner">{state.error}</div>}

        <div className="pdm-filterbar">
          <div className="pdm-field">
            <label className="pdm-filter-required">From</label>
            <DatePicker className="pdm-datepicker" selected={f.startDate} onChange={(v) => this.onFilterChange('startDate', v)} dateFormat="dd/MM/yyyy" maxDate={f.endDate} />
          </div>
          <div className="pdm-field">
            <label className="pdm-filter-required">To</label>
            <DatePicker className="pdm-datepicker" selected={f.endDate} onChange={(v) => this.onFilterChange('endDate', v)} dateFormat="dd/MM/yyyy" minDate={f.startDate} maxDate={new Date()} />
          </div>
          <div className="pdm-field">
            <label>Date Basis</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.dateType} options={DATE_TYPE_OPTIONS} onChange={(v) => this.onFilterChange('dateType', v)} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Entity</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.entity} options={fo.entity} onChange={(v) => this.onFilterChange('entity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporter} options={fo.transporter} onChange={(v) => this.onFilterChange('transporter', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Vehicle Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.vehicleType} options={fo.vehicleType} onChange={(v) => this.onFilterChange('vehicleType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceZone} options={fo.sourceZone} onChange={(v) => this.onFilterChange('sourceZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceCity} options={fo.sourceCity} onChange={(v) => this.onFilterChange('sourceCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationZone} options={fo.destinationZone} onChange={(v) => this.onFilterChange('destinationZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationCity} options={fo.destinationCity} onChange={(v) => this.onFilterChange('destinationCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination State</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationState} options={fo.destinationState} onChange={(v) => this.onFilterChange('destinationState', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Fuel Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.fuelType} options={fo.fuelType} onChange={(v) => this.onFilterChange('fuelType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter Category</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporterCategory} options={fo.transporterCategory} onChange={(v) => this.onFilterChange('transporterCategory', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Invoice Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.invoiceType} options={fo.invoiceType} onChange={(v) => this.onFilterChange('invoiceType', v || [])} />
          </div>
          <div className="pdm-filter-toolbar">
            <button className="pdm-btn pdm-btn-ghost" onClick={this.onReset}>Reset</button>
            <button className="pdm-btn pdm-btn-primary" onClick={this.onApply}>Apply Filters</button>
          </div>
        </div>

        {!d ? (
          <div className="pdm-card">{this.renderEmptyState('Loading FTL movement data...', null)}</div>
        ) : (
          <>
            <div className="pdm-kpi-grid">
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">FTL Records (period)</div>
                <div className="pdm-kpi-value">{d.totalRecords}</div>
                <div className="pdm-kpi-sub">Current filters</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Total Trips (period)</div>
                <div className="pdm-kpi-value">{d.totalTrips}</div>
                <div className="pdm-kpi-sub">Distinct vehicle + dispatch-date combinations</div>
              </div>
            </div>

            <div className="pdm-section">
              {this.renderChartCard(
                'Fleet Utilization (%) Overview',
                'Weight Utilization % by vehicle type. Volume Utilization % uses max observed CBM per vehicle type as a proxy (no rated capacity table exists).',
                this.buildFleetUtilizationOptions(d.fleetUtilization),
                340,
              )}
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Route Wise Utilization (%) (Monthly)</h2>
                  <p>Volume % uses max observed CBM per vehicle type as a proxy (no rated capacity table exists).</p>
                </div>
              </div>
              <GridCard title="Route Wise Utilization (%) (Monthly)" loading={state.loading} onRefresh={this.loadData} height={480} {...this.buildRouteUtilizationGrid(d)} />
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                <GridCard
                  title="Vehicle Master Wise Weights (KG) and CBM"
                  loading={state.loading}
                  onRefresh={this.loadData}
                  footerNote='Wt (KG) is the max vehicle_capacity seen per vehicle type. Vol (CBM) is the max total_cbm actually carried per vehicle type - a proxy, since no rated CBM capacity table exists.'
                  {...this.buildVehicleMasterGrid(d)}
                />
                <GridCard title="Total Trips (Route)" loading={state.loading} onRefresh={this.loadData} {...this.buildTotalTripsRouteGrid(d)} />
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-grid-2">
                <GridCard title="Transporter Wise Total Trips (Monthly)" loading={state.loading} onRefresh={this.loadData} {...this.buildTransporterTripsGrid(d)} />
                <GridCard title="Vehicle Type Wise Total Trips (Monthly)" loading={state.loading} onRefresh={this.loadData} {...this.buildVehicleTypeTripsGrid(d)} />
              </div>
            </div>

            <div className="pdm-section">
              <div className="pdm-card">
                <div className="pdm-card-toolbar" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <p className="pdm-card-title" style={{ margin: 0 }}>Trend Analysis - Weight (KG) & CBM Transported (Route)</p>
                    <p className="pdm-card-subtitle" style={{ margin: '2px 0 0' }}>Month-wise. Defaults to all routes combined.</p>
                  </div>
                  <div style={{ minWidth: 220 }}>
                    <Select styles={MULTI_SELECT_STYLES} value={state.trendRoute} options={d.routeOptions} onChange={this.onTrendRouteChange} />
                  </div>
                </div>
                {this.renderChartCard(null, null, this.buildWeightCbmTrendOptions(d.weightCbmTrend), 340)}
              </div>
            </div>

            <div className="pdm-section">
              {this.renderChartCard(
                'Trend Analysis - Weight & Volume Utilization (%)',
                'Month-wise. Volume Utilization % uses max observed CBM per vehicle type as a proxy (no rated capacity table exists).',
                this.buildUtilizationTrendOptions(d.utilizationTrend),
                340,
              )}
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Summary</h2>
                  <p>Full FTL shipment-level detail for the selected filters. "Trips" is 1 on each trip's first row only, so the footer total is the correct distinct trip count.</p>
                </div>
              </div>
              <GridCard
                title="Summary"
                loading={state.loading}
                onRefresh={this.loadData}
                height={600}
                records={d.summaryRows}
                columnDefs={this.buildSummaryColumnDefs()}
                pinnedBottomRowData={[{
                  dispatch_date: null,
                  transporter_name: 'Total',
                  total_weight: round1(sum(d.summaryRows.map(weightKgOf))),
                  total_cbm: round1(sum(d.summaryRows.map(cbmOf))),
                  vehicle_capacity: round1(sum(d.summaryRows.map(vehicleCapacityOf))),
                  __tripFlag: sum(d.summaryRows.map((r) => r.__tripFlag)),
                }]}
              />
            </div>
          </>
        )}
      </>
    );
  }
}
